<template>
  <rmp-base-modal
    v-model="isOpen"
    extra-classes="ScoresSettingsModal Modal--large"
  >
    <template #modal-header>
      <h2 class="u-text-center">
        Scores
      </h2>
    </template>
    <template #modal-body>
      <div
        v-if="questionList.length > 0"
        class="Scores-container"
      >
        <div
          v-for="(question, qIndex) in questionList"
          :key="question.question_id"
          class="Scores-section"
        >
          <h2>{{ question.label }}</h2>
          <div
            v-if="question.showScores"
            class="Options-container"
          >
            <div
              v-for="(option, oIndex) in question.options"
              :key="oIndex"
              class="option"
            >
              <rmp-input
                v-model="option.label"
                :disabled="true"
              />
              <rmp-input
                v-model.number="option.score"
                input-type="number"
                :error="checkInput(qIndex, oIndex)"
                :manual-validate="validateAll"
              />
            </div>
          </div>
          <a
            v-if="!question.showScores"
            class="Scores-button add"
            @click="question.showScores = true"
          >Add Scores</a>
          <a
            v-else
            class="Scores-button remove"
            @click="question.showScores = false"
          >Remove Scores</a>
        </div>
      </div>
      <div
        v-else
        class="Scores-container"
      >
        <h4>
          No questions in this webform support scoring
        </h4>
      </div>
    </template>
    <template #modal-footer>
      <div class="u-floatLeft">
        <button
          class="Button Button--green-outline"
          @click="isOpen = false"
        >
          Cancel
        </button>
      </div>
      <div class="u-floatRight">
        <button
          class="Button Button--green"
          :disabled="saveButtonDisabled"
          @click="saveScores"
        >
          Save
        </button>
      </div>
    </template>
  </rmp-base-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { required, minValue } from 'vuelidate/lib/validators';
import RmpBaseModal from '@rmpenterprise/base-modal';
import RmpInput from '@rmpenterprise/form-input';
import ModalActionsMixin from './mixins/modal-actions-mixin';
import ValidationMixin from '../../../mixins/validation-mixin';

export default {
  components: {
    RmpBaseModal,
    RmpInput,
  },
  mixins: [ModalActionsMixin, ValidationMixin],
  data() {
    return {
      questionList: [],
      saveButtonDisabled: false,
      validateAll: false,
    };
  },
  computed: {
    scoreInputs() {
      return this.questionList.filter((q) => q.showScores === true);
    },
  },
  mounted() {
    this.getEventScores(this.$route.params.id)
      .then((response) => {
        this.$emit('scores-settings:responseSuccess', response.meta.maxScorePerResponse);
        this.questionList = response.data;

        // add showScores to each question to keep track if scores should be shown or not
        this.questionList = this.questionList.map((q) => ({ ...q, showScores: true }));

        // set scores to 0 if null
        this.questionList.forEach((q, qIndex) => {
          q.options.forEach((o, oIndex) => {
            if (o.score == null) {
              this.questionList[qIndex].options[oIndex].score = 0;
            }
          });
        });
      });
  },
  methods: {
    ...mapActions('events', ['getEventScores', 'saveScoresSettings']),
    saveScores() {
      this.validateAll = true;
      if (this.$v.$invalid) {
        return;
      }
      this.validateAll = false;

      this.saveButtonDisabled = true;

      const payload = {
        configuration: this.scoreInputs.map((q) => ({
          question_id: q.question_id,
          scores: q.options.map((o) => ({
            value: o.value,
            score: o.score,
          })),
        })),
      };

      this.saveScoresSettings({ eventID: this.$route.params.id, payload })
        .then(() => {
          // update maxScore on logic button
          let maxScore = 0;
          this.scoreInputs.forEach((question) => {
            maxScore += Math.max(...question.options.map((o) => o.score));
          });

          this.$emit('scores-settings:responseSuccess', maxScore);

          this.isOpen = false;
          this.$bus.$emit('displayMsg', {
            text: 'Scores successfully updated',
            type: 'success',
          });
        })
        .catch((error) => {
          this.$bus.$emit('displayMsg', {
            text: error.data.message,
            type: 'error',
          });
        })
        .finally(() => {
          this.saveButtonDisabled = false;
        });
    },
    // checkInput is used to check the validation status of score inputs
    checkInput(qIndex, oIndex) {
      const question = this.questionList[qIndex];
      const index = this.scoreInputs.findIndex((s) => s.question_id === question.question_id);

      return this.validateStatus(this.$v.scoreInputs.$each[index].options.$each[oIndex].score, 'Score');
    },
  },
  validations() {
    return {
      scoreInputs: {
        $each: {
          options: {
            $each: {
              score: {
                required,
                minValue: minValue(0),
              },
            },
          },
        },
      },
    };
  },
};
</script>
<style lang="less" scoped>
.Scores-container {
  padding-top: 20px;

  .Scores-section {
    width: 100%;
    min-height: 50px;
    padding: 25px;
    margin-bottom: 20px;
    border: 1px solid #b6b6b6;
    border-radius: 3px;
    display: inline-block;

    .option {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
      column-gap: 10px;

      .Form-field {
        margin-bottom: 0;
      }
    }

    .Scores-button {
      text-decoration: underline;
      cursor: pointer;
    }

    .remove {
        color: red;
        float: right;
    }
  }
}
</style>
